<template>
  <div class="chart-controls">
    <button
      class="chart-controls__paginate chart-controls__paginate--prev"
      @click="paginateDate(-1)"
      :disabled="false"
    >
      <span class="visually-hidden">{{ t('global.back') }}</span>
      <IconCaret />
    </button>
    <DatePicker
      :timespan="timespan"
      :date="selectedDate"
      :min-date="minDate"
      :max-date="maxDate"
      @date-changed="(newDate) => setDate(newDate)"
    />
    <button
      v-if="showToday"
      class="button button--secondary chart-controls__today"
      @click="setToToday"
    >
      {{ t('global.today') }}
    </button>
    <Choice
      v-if="showTimeSpan"
      name="timespan"
      :default="timespan"
      :items="timespanChoices"
      @selection-changed="setTimespan"
    />
    <button
      class="chart-controls__paginate chart-controls__paginate--next"
      @click="paginateDate(1)"
      :disabled="false"
    >
      <span class="visually-hidden">{{ t('global.next') }}</span>
      <IconCaret />
    </button>
  </div>
</template>

<script lang="ts">
  import { useDate } from '@/composables/useDate.ts';
  const dateHelper = useDate()
</script>
<script setup lang="ts">
  import { useStrings } from '@/composables/useStrings.ts';
  import { computed, ref, type Ref } from 'vue';

  import Choice from '@/components/Choice.vue';
  import DatePicker from '@/components/DatePicker.vue';
  import IconCaret from '@/components/icons/Caret.vue';

  interface DatePickerValue {
    "year": number,
    "month": number
  }


  const { t } = useStrings()

  const emit = defineEmits(['dateChange', 'timespanChange'])
  const props = defineProps({
    date: {
      type: Date,
      default: new Date(),
    },
    timespan: {
      type: String,
      default: dateHelper.TIMESPAN.DAY,
      validator(value) {
        return (
          value === dateHelper.TIMESPAN.DAY ||
          value === dateHelper.TIMESPAN.MONTH
        )
      },
    },
    showToday: {
      type: Boolean,
      default: false,
    },
    showTimeSpan: {
      type: Boolean,
      default: false,
    },
    earliestDate: {
      type: Date,
      required: false,
      // Defaulting to the unix epoch to effectively not set a lower limit.
      // TODO Set this to the date when the cube/contract was activated?
      default: () => new Date('1970-01-01'),
    },
    latestDate: {
      type: Date,
      required: false,
      // allow this month and next (for viewing the float price)
      default: () => dateHelper.nextMonth(dateHelper.nextMonth()),
    }
  })

  const selectedDate: Ref<DatePickerValue | Date> = ref(props.date)
  const timespan = ref(props.timespan)

  const maxDate = ref(props.latestDate)
  // We're not making mitDate reactive since it should not be needed.
  const minDate = ref(props.earliestDate)

  const setDate = (newValue) => {
    selectedDate.value = newValue
    onDateChanged(newValue)
  }

  const isMonthView = computed(() => timespan.value === dateHelper.TIMESPAN.MONTH)

  const setToToday = () => {
    const today = dateHelper.today()
    if (isMonthView.value) {
      setDate({
        month: today.getMonth(),
        year: today.getFullYear(),
      })
    }
    else {
      setDate(today)
    }
  }

  const setTimespan = (newValue) => {
    timespan.value = newValue
    onTimespanChanged()
  }

  const onDateChanged = (newValue: Date) => {
    if (timespan.value === dateHelper.TIMESPAN.MONTH) {
      emit('dateChange', dateHelper.moment(selectedDate.value).startOf('month'))
    }
    else {
      emit('dateChange', selectedDate.value)
    }
  }

  const onTimespanChanged = () => {
    emit('timespanChange', timespan.value)
    if (timespan.value === dateHelper.TIMESPAN.MONTH) {
      setDate({
        month: parseInt(dateHelper.formatDate(selectedDate.value, 'MONTH_NUMERIC')) - 1,
        year: parseInt(dateHelper.formatDate(selectedDate.value, 'YEAR')),
      })
    } else {
      const { year, month } = (selectedDate.value as DatePickerValue)
      setDate(new Date(`${year}-${month+1}-01`))
    }
  }

  const timespanChoices = [
    {
      title: t('timespans.day'),
      value: dateHelper.TIMESPAN.DAY,
    },
    {
      title: t('timespans.month'),
      value: dateHelper.TIMESPAN.MONTH,
    },
  ]

  const paginateDate = (offset) => {
    if (offset < -1) {
      offset = -1
    }
    else if (offset > 1) {
      offset = 1
    }
    else if (offset === 0) {
      return
    }

    if (timespan.value === dateHelper.TIMESPAN.MONTH) {
      // If the timespan is months, the datepicker uses an object in this form
      // {
      //   month: 0-11,
      //   year: 2025,
      // }
      // Update this object depending on the value of offset.

      const date = selectedDate.value as DatePickerValue;

      const dateCopy = {
        month: date.month + offset,
        year: date.year,
      }

      if (dateCopy.month < 0) {
        dateCopy.month = 11
        dateCopy.year -= 1
      }
      else if (dateCopy.month > 11) {
        dateCopy.month = 0
        dateCopy.year += 1
      }
      selectedDate.value = dateCopy
    }
    else {
      selectedDate.value = new Date(dateHelper.applyOffset(
        selectedDate.value as Date,
        offset,
        timespan.value,
      ))
    }
  }

</script>

<style lang="scss">
$controls-height: 2rem;

.chart-controls {
  display: flex;
  align-items: stretch;
  position: relative;
  z-index: 10;
  margin-top: $spacer05;

  @media (max-width: ($bp-sm - 1px)) {
    display: grid;
    grid-gap: $spacer10;
    grid-template-columns: repeat(6, 1fr);
  }

  .radios {
    grid-column: span 4/-1;

    @media (min-width: $bp-sm) {
      margin-right: $spacer10;
    }

    &__item {
      height: 100%;
      flex-basis: 50%;
      width: 50%;
      display: inline-flex;
      justify-content: center;
      align-items: center;
    }

    &__item--active {
      &-day,
      &-month {
        background: $community;
        border-color: $community;
      }
    }
  }


  .date-picker {
    transition: $transition-base;
    $border-radius: 2px;
    background-color: $white;

    @media (max-width: ($bp-sm - 1px)) {
      grid-row: 2;
      width: 100%;
      grid-column: span 4;
      margin-left: auto;
      margin-right: auto;
    }

    @media (min-width: $bp-sm) {
      margin: 0 $spacer10;
    }

  }

  &__paginate {
    display: inline-block;
    background: $grey-lighter;
    color: $grey-dark;
    border-radius: $border-radius;
    padding: 0 $spacer10;
    height: 2.25rem;
    vertical-align: middle;
    transition: $transition-base;
    cursor: pointer;
    line-height: $controls-height;
    appearance: none;
    border: 0;
    grid-row: 2;

    &:disabled {
      opacity: 0.5;
      cursor: default;

      @include hover-focus-active {
        color: $grey-dark;
        background: $grey-lighter;
      }
    }

    .icon {
      vertical-align: middle;
      width: $spacer10;
      transform: translateX(0.12em) translateY(-0.1em) rotate(0deg);

      @media (min-height: 500px) and (min-width: $bp-xs) {
        width: $spacer10;
      }

      @media (min-width: $bp-md) {
        width: $spacer20;
      }
    }

    &--prev {
      margin-right: auto;

      .icon {
        transform: translateX(-0.12em) translateY(-0.1em) rotate(180deg);
      }
    }

    &--next {
      margin-left: auto;
    }
  }

  &__today {
    border-radius: $border-radius;
    grid-column: span 3;

    @media (max-width: ($bp-xs - 1px)) {
      padding: $spacer025 $spacer05;
    }

    @media (min-width: $bp-sm) {
      margin-left: $spacer10;
      margin-right: $spacer10;
    }
  }
}



</style>
